<template>
  <div class="my-photos-view">
    <div class="page-header">
      <h2>我的图片</h2>
      <router-link to="/upload" class="upload-btn">
        <i class="fas fa-upload"></i>
        上传新图片
      </router-link>
    </div>

    <div class="photos-grid" v-if="photos.length">
      <div v-for="photo in photos" :key="photo.id" class="photo-item">
        <router-link :to="`/photos/${photo.imageId}`" class="photo-link">
          <div class="photo-preview">
            <img :src="photo.imageUrl" :alt="photo.imageName">
            <div class="status-badge"
              :class="getStatusClass(photo.auditStatus)"
              @mouseenter="photo.auditStatus === -1 && showTooltip(photo, $event)"
              @mouseleave="hideTooltip"
            >
              {{ getStatusText(photo.auditStatus) }}
              <i v-if="photo.auditStatus === -1" class="fas fa-exclamation-circle error-icon"></i>
            </div>
            <div class="photo-info">
              <h3>{{ photo.imageName }}</h3>
              <div class="photo-tags">
                <span v-for="tag in photo.tags ? photo.tags.split(',') : []" :key="tag" class="tag">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <div class="empty-state" v-else>
      <i class="fas fa-images"></i>
      <p>还没有上传过图片</p>
      <router-link to="/upload" class="upload-btn">
        立即上传
      </router-link>
    </div>

    <div class="pagination" v-if="total > pageSize">
      <button
        class="page-btn"
        :disabled="pageNum === 1"
        @click="changePage(pageNum - 1)"
      >
        <i class="fas fa-chevron-left"></i>
      </button>
      <span class="page-info">{{ pageNum }} / {{ totalPages }}</span>
      <button
        class="page-btn"
        :disabled="pageNum === totalPages"
        @click="changePage(pageNum + 1)"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- 添加悬浮提示框 -->
    <div class="tooltip" v-show="showingTooltip" :style="tooltipStyle">
      <div class="tooltip-arrow"></div>
      <div class="tooltip-content">
        <i class="fas fa-times-circle"></i>
        <span>{{ currentTooltip }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '@/utils/request'

export default {
  name: 'MyPhotosView',
  data() {
    return {
      photos: [],
      pageNum: 1,
      pageSize: 12,
      total: 0,
      loading: false,
      showingTooltip: false,
      currentTooltip: '',
      tooltipStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.fetchPhotos()
  },
  methods: {
    async fetchPhotos() {
      if (this.loading) return
      this.loading = true

      try {
        const response = await request(`/api/image/my/list?pageNum=${this.pageNum}&pageSize=${this.pageSize}`)

        if (response.ok) {
          this.photos = response.data.list
          this.total = response.data.total
        } else {
          throw new Error(response.msg || '获取图片列表失败')
        }
      } catch (error) {
        console.error('获取图片列表失败:', error)
        alert('获取图片列表失败，请重试')
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      this.pageNum = page
      this.fetchPhotos()
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    getStatusClass(status) {
      const statusMap = {
        0: 'status-pending',    // 待审核
        1: 'status-approved',   // 已通过
        '-1': 'status-rejected'    // 已拒绝
      }
      return statusMap[status] || 'status-pending'
    },
    getStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '已通过',
        '-1': '已拒绝'
      }
      return statusMap[status] || '待审核'
    },
    showTooltip(photo, event) {
      if (photo.auditRemark) {
        this.currentTooltip = photo.auditRemark
        this.showingTooltip = true

        // 计算提示框位置，与状态标签对齐
        const rect = event.target.getBoundingClientRect()
        this.tooltipStyle = {
          top: `${rect.top + window.scrollY}px`,
          left: `${rect.right + window.scrollX + 10}px` // 在状态标签右侧显示
        }
      }
    },
    hideTooltip() {
      this.showingTooltip = false
    }
  }
}
</script>

<style scoped>
.my-photos-view {
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.upload-btn {
  padding: 8px 16px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  background: #43A047;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.photo-item {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.photo-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.photo-preview {
  position: relative;
  padding-top: 75%;
}

.photo-preview img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  color: white;
}

.photo-info h3 {
  margin: 0;
  font-size: 16px;
  margin-bottom: 8px;
}

.photo-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 2px 8px;
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  font-size: 12px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  color: #ddd;
  margin-bottom: 16px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 30px;
}

.page-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  border-color: #4CAF50;
  color: #4CAF50;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.photo-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  z-index: 2;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: help;
}

.status-pending {
  background: rgba(255, 152, 0, 0.9);
  color: white;
}

.status-approved {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.status-rejected {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}

.error-icon {
  color: white;
  font-size: 14px;
}

.tooltip {
  position: absolute;
  z-index: 1000;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 0;
  max-width: 300px;
  pointer-events: none;
}

.tooltip-arrow {
  position: absolute;
  top: 50%;
  left: -6px; /* 改为左侧 */
  transform: translateY(-50%) rotate(-45deg); /* 调整箭头方向 */
  width: 12px;
  height: 12px;
  background: #f44336;
  transform-origin: center;
}

.tooltip-content {
  position: relative;
  background: #f44336;
  color: white;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.4;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.tooltip-content i {
  margin-top: 3px;
  font-size: 16px;
}

@media (max-width: 768px) {
  .my-photos-view {
    margin: 20px auto;
  }

  .photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
  }

  .photo-info h3 {
    font-size: 14px;
  }

  .status-badge {
    top: 8px;
    left: 8px;
    padding: 3px 10px;
    font-size: 11px;
  }

  .tooltip {
    max-width: calc(100vw - 32px); /* 移动端下限制最大宽度 */
    left: 16px !important; /* 强制在左侧显示 */
    right: 16px !important;
  }

  .tooltip-arrow {
    left: 20px; /* 移动端下固定箭头位置 */
  }

  .tooltip-content {
    padding: 10px 14px;
    font-size: 13px;
  }
}
</style>
